// src/utils/routes.ts

// 1. Los PATHS puros que usará React Router
export const PATHS = {
  localeRoot: "/:lang",
  home: "/:lang/",
  faq: "/:lang/faq",
  portfolio: "/:lang/portfolio",
  plans: "/:lang/plans", // redirect → el index (página retirada)
  threeSixty: "/:lang/360", // redirect → el index (landing única)
  threeSixtyPortfolio: "/:lang/360/portfolio", // redirect → portfolio
  bookDemo: "/:lang/book", // company-level demo / contact request page
  privacy: "/:lang/privacy",
  terms: "/:lang/terms",
  viewer: "/360/:shortId",
  ownerHub: "/hub/:username", // public owner hub by username (all plans, no locale)
  login: "/login",
  register: "/register",
  authCallback: "/auth/callback",
  resetPassword: "/auth/reset", // forgot-password request + recovery set-new-password
  overview: "/dashboard",
  tours: "/dashboard/tours",
  detail: "/dashboard/tours/:id",
  edit: "/edit/:id",               // legacy redirect only
  legacyTourEdit: "/dashboard/tours/edit/:id", // old /tours/edit/:id → /tours/:id
  settings: "/dashboard/settings",
  users: "/dashboard/users",
  userDetail: "/dashboard/users/:id",
  bookings: "/dashboard/bookings",
  bookingResponse: "/booking-response",
  branding: "/dashboard/branding",
  billing: "/dashboard/billing",
  analytics: "/dashboard/analytics",
  health: "/dashboard/health",
  leads: "/dashboard/leads",
  leadDetail: "/dashboard/leads/:source/:id",
  contacts: "/dashboard/contacts",
  contactDetail: "/dashboard/contacts/:id",
  help: "/dashboard/help",
  helpTopic: "/dashboard/help/:topic",
  showcase: "/dashboard/showcase", // dev-only design-system gallery
} as const;

// 2. Los BUILDERS funcionales para generar URLs reales en tus componentes
export const buildRoute = {
  home: (lang: string) => `/${lang}/`,
  faq: (lang: string) => `/${lang}/faq`,
  portfolio: (lang: string) => `/${lang}/portfolio`,
  // Landing única: la landing .360 ES el index.
  threeSixty: (lang: string) => `/${lang}/`,
  threeSixtyPortfolio: (lang: string) => `/${lang}/portfolio`,
  bookDemo: (lang: string, source?: string) =>
    `/${lang}/book${source ? `?source=${source}` : ""}`,
  privacy: (lang: string) => `/${lang}/privacy`,
  terms: (lang: string) => `/${lang}/terms`,
  viewer: (shortId: string) => `/360/${shortId}`,
  ownerHub: (username: string) => `/hub/${username}`,
  login: () => PATHS.login,
  register: () => PATHS.register,
  authCallback: () => PATHS.authCallback,
  resetPassword: () => PATHS.resetPassword,
  overview: () => PATHS.overview,
  tours: () => PATHS.tours,
  // Editor deep-links: `?section=` = nav de primer nivel. Los tours son planos.
  edit: (id: string, section?: string) =>
    `/dashboard/tours/${id}${section ? `?section=${section}` : ""}`,
  detail: (id: string, section?: string) =>
    `/dashboard/tours/${id}${section ? `?section=${section}` : ""}`,
  settings: () => PATHS.settings,
  users: () => PATHS.users,
  userDetail: (id: string, section?: string) =>
    `/dashboard/users/${id}${section ? `?section=${section}` : ""}`,
  // Optional status deep-link, used by the analytics donuts. Namespaced key —
  // BookingsPage reads its query state under the "bk" namespace.
  bookings: (status?: string) =>
    `${PATHS.bookings}${status ? `?bk.f.status=${status}` : ""}`,
  bookingResponse: () => PATHS.bookingResponse,
  branding: () => PATHS.branding,
  billing: () => PATHS.billing,
  analytics: (focus?: string) =>
    `${PATHS.analytics}${focus ? `?focus=${focus}` : ""}`,
  health: (resourceId?: string) =>
    `${PATHS.health}${resourceId ? `?resource=${encodeURIComponent(resourceId)}` : ""}`,
  // LeadsPage uses the default (unnamespaced) query state, so `f.status`.
  leads: (status?: string) =>
    `${PATHS.leads}${status ? `?f.status=${status}` : ""}`,
  contacts: (status?: string) =>
    `${PATHS.contacts}${status ? `?f.status=${status}` : ""}`,
  contactDetail: (id: string) => `/dashboard/contacts/${id}`,
  help: (topic?: string, anchor?: string) =>
    topic ? `${PATHS.help}/${topic}${anchor ? `#${anchor}` : ""}` : PATHS.help,
  showcase: () => PATHS.showcase,
};
